import { useApolloClient, useMutation } from '@apollo/client';
import { Modal, OfficeButton, SelectInput, toast } from 'components';
import {
  currentCountryVar,
  currentTokenVar,
  useCurrentUser,
} from '_graphql/cache/auth';
import { CountryCode } from '_graphql/__generated__/globalTypes';
import {
  ArrowDownTrayIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { read, utils } from 'xlsx';
import {
  BulkProductUploadColumnMapping,
  normalizeBulkUploadHeader,
  parseBulkProductUploadWithMapping,
  suggestBulkUploadColumnMapping,
} from '../utils/parseBulkProductUpload';
import { bulkRowsToTempCreateProductInputs } from '../utils/bulkRowsToTempCreateProductInput';
import { buildBulkProductUploadTemplateXlsx } from '../utils/buildBulkProductUploadTemplate';
import { emptyBulkProductVariationColumnMapping } from '../utils/bulkVariationColumnParse';
import { enrichSupplierBulkProductInputs } from '../utils/enrichSupplierBulkProductInputs';
import { rehostBulkProductMediaUrls } from '../utils/rehostBulkProductMediaUrls';
import { fetchAllProductCategories } from '../utils/fetchAllProductCategories';
import {
  buildTagNameToCanonical,
  fetchAllProductTags,
} from '../utils/fetchAllProductTags';
import { parseCurrencyOptionsFromOperatingCountryRows } from '../utils/fetchOrganizationCurrencies';
import { BULK_CREATE_PRODUCTS } from '_graphql/mutation/products';
import type {
  BulkCreateProducts,
  BulkCreateProductsVariables,
} from '_graphql/mutation/__generated__/BulkCreateProducts';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  refetchProducts: () => void | Promise<unknown>;
};

type Phase = 'pick' | 'map';

const PREVIEW_ROW_LIMIT = 25;

const MAPPING_SELECT_IDS = {
  title: 'bulkMapTitle',
  description: 'bulkMapDescription',
  sku: 'bulkMapSku',
  parentSku: 'bulkMapParentSku',
  combinationString: 'bulkMapCombinationString',
  price: 'bulkMapPrice',
  costPrice: 'bulkMapCost',
  quantity: 'bulkMapQty',
  imageUrl: 'bulkMapImg',
  avatarUrl: 'bulkMapAvatar',
  currency: 'bulkMapCurrency',
  published: 'bulkMapPublished',
  weight: 'bulkMapWeight',
  tags: 'bulkMapTags',
  limitedStock: 'bulkMapLimitedStock',
  inhouse: 'bulkMapInhouse',
  categoryName: 'bulkMapCategoryName',
  catalogIds: 'bulkMapCatalogIds',
  variationName1: 'bulkMapVarName1',
  variationOptions1: 'bulkMapVarOpt1',
  variationName2: 'bulkMapVarName2',
  variationOptions2: 'bulkMapVarOpt2',
  variationName3: 'bulkMapVarName3',
  variationOptions3: 'bulkMapVarOpt3',
  variationName4: 'bulkMapVarName4',
  variationOptions4: 'bulkMapVarOpt4',
  variationName5: 'bulkMapVarName5',
  variationOptions5: 'bulkMapVarOpt5',
  variationName6: 'bulkMapVarName6',
  variationOptions6: 'bulkMapVarOpt6',
  variationName7: 'bulkMapVarName7',
  variationOptions7: 'bulkMapVarOpt7',
} as const;

const BULK_VARIATION_NAME_OPTIONS_SELECT_IDS = [
  [1, MAPPING_SELECT_IDS.variationName1, MAPPING_SELECT_IDS.variationOptions1],
  [2, MAPPING_SELECT_IDS.variationName2, MAPPING_SELECT_IDS.variationOptions2],
  [3, MAPPING_SELECT_IDS.variationName3, MAPPING_SELECT_IDS.variationOptions3],
  [4, MAPPING_SELECT_IDS.variationName4, MAPPING_SELECT_IDS.variationOptions4],
  [5, MAPPING_SELECT_IDS.variationName5, MAPPING_SELECT_IDS.variationOptions5],
  [6, MAPPING_SELECT_IDS.variationName6, MAPPING_SELECT_IDS.variationOptions6],
  [7, MAPPING_SELECT_IDS.variationName7, MAPPING_SELECT_IDS.variationOptions7],
] as const;

const COUNTRY_CODES = new Set<string>(Object.values(CountryCode));

function parseDefaultCountry(raw: string): CountryCode | null {
  const u = raw?.trim().toUpperCase();
  if (u && COUNTRY_CODES.has(u)) {
    return u as CountryCode;
  }
  return null;
}

type BulkRefData = {
  categoryNameToId: Map<string, string>;
  categoryIdToName: Map<string, string>;
  tagNameToCanonical: Map<string, string> | null;
};

const BulkUploadProductsModal: FC<Props> = ({
  open,
  setOpen,
  refetchProducts,
}) => {
  const client = useApolloClient();
  const user = useCurrentUser();
  const country = user?.country ?? '';
  const [bulkRef, setBulkRef] = useState<BulkRefData | null>(null);

  const [bulkCreateProductsMutation] = useMutation<
    BulkCreateProducts,
    BulkCreateProductsVariables
  >(BULK_CREATE_PRODUCTS, {
    refetchQueries: ['GetProducts'],
  });
  const [phase, setPhase] = useState<Phase>('pick');
  const [loadingFile, setLoadingFile] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [submittingUpload, setSubmittingUpload] = useState(false);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rawRows, setRawRows] = useState<Record<string, unknown>[]>([]);
  const [mapping, setMapping] = useState<BulkProductUploadColumnMapping>({
    title: '',
    description: '',
    sku: '',
    parentSku: '',
    combinationString: '',
    ...emptyBulkProductVariationColumnMapping(),
    price: '',
    costPrice: '',
    quantity: '',
    imageUrl: '',
    avatarUrl: '',
    currency: '',
    published: '',
    weight: '',
    tags: '',
    limitedStock: '',
    inhouse: '',
    categoryName: '',
    catalogIds: '',
  });

  useEffect(() => {
    if (!open) {
      setLoadingFile(false);
      setParseErrors([]);
      setFileName(null);
      setPhase('pick');
      setRawRows([]);
      setMapping({
        title: '',
        description: '',
        sku: '',
        parentSku: '',
        combinationString: '',
        ...emptyBulkProductVariationColumnMapping(),
        price: '',
        costPrice: '',
        quantity: '',
        imageUrl: '',
        avatarUrl: '',
        currency: '',
        published: '',
        weight: '',
        tags: '',
        limitedStock: '',
        inhouse: '',
        categoryName: '',
        catalogIds: '',
      });
      setTemplateLoading(false);
      setSubmittingUpload(false);
      setBulkRef(null);
    }
  }, [open]);

  const currencyOpts = useMemo(() => {
    const dc = parseDefaultCountry(country);
    if (!dc) {
      return null;
    }
    return parseCurrencyOptionsFromOperatingCountryRows([], dc);
  }, [country]);

  useEffect(() => {
    if (!open || !country || !user?.id) {
      setBulkRef(null);
      return;
    }
    let cancelled = false;
    const empty: BulkRefData = {
      categoryNameToId: new Map(),
      categoryIdToName: new Map(),
      tagNameToCanonical: null,
    };
    (async () => {
      try {
        const [categories, tagNames] = await Promise.all([
          fetchAllProductCategories(client, {
            country,
            supplierId: user.id,
          }),
          fetchAllProductTags(client, { country, supplierId: user.id }),
        ]);
        if (cancelled) {
          return;
        }
        const categoryNameToId = new Map<string, string>();
        const categoryIdToName = new Map<string, string>();
        for (const c of categories) {
          categoryNameToId.set(normalizeBulkUploadHeader(c.name), c.id);
          categoryIdToName.set(c.id, c.name);
        }
        setBulkRef({
          categoryNameToId,
          categoryIdToName,
          tagNameToCanonical:
            tagNames.length > 0 ? buildTagNameToCanonical(tagNames) : null,
        });
      } catch {
        if (!cancelled) {
          setBulkRef(empty);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, country, client, user?.id]);

  const columnOptions = useMemo(() => {
    if (!rawRows.length) {
      return [];
    }
    return Object.keys(rawRows[0]).map((h) => ({
      label: h,
      value: h,
    }));
  }, [rawRows]);

  const selectOptionsRequired = useMemo(
    () => [{ label: '— Select column —', value: '' }, ...columnOptions],
    [columnOptions]
  );

  const selectOptionsImage = useMemo(
    () => [{ label: '— None —', value: '' }, ...columnOptions],
    [columnOptions]
  );

  const selectValues = useMemo(() => {
    const v: Record<string, string> = {
      [MAPPING_SELECT_IDS.title]: mapping.title,
      [MAPPING_SELECT_IDS.description]: mapping.description,
      [MAPPING_SELECT_IDS.sku]: mapping.sku,
      [MAPPING_SELECT_IDS.parentSku]: mapping.parentSku,
      [MAPPING_SELECT_IDS.combinationString]: mapping.combinationString,
      [MAPPING_SELECT_IDS.price]: mapping.price,
      [MAPPING_SELECT_IDS.costPrice]: mapping.costPrice,
      [MAPPING_SELECT_IDS.quantity]: mapping.quantity,
      [MAPPING_SELECT_IDS.imageUrl]: mapping.imageUrl,
      [MAPPING_SELECT_IDS.avatarUrl]: mapping.avatarUrl,
      [MAPPING_SELECT_IDS.currency]: mapping.currency,
      [MAPPING_SELECT_IDS.published]: mapping.published,
      [MAPPING_SELECT_IDS.weight]: mapping.weight,
      [MAPPING_SELECT_IDS.tags]: mapping.tags,
      [MAPPING_SELECT_IDS.limitedStock]: mapping.limitedStock,
      [MAPPING_SELECT_IDS.inhouse]: mapping.inhouse,
      [MAPPING_SELECT_IDS.categoryName]: mapping.categoryName,
      [MAPPING_SELECT_IDS.catalogIds]: mapping.catalogIds,
    };
    for (const [
      n,
      nameId,
      optionsId,
    ] of BULK_VARIATION_NAME_OPTIONS_SELECT_IDS) {
      v[nameId] = mapping[`variationName${n}`];
      v[optionsId] = mapping[`variationOptions${n}`];
    }
    return v;
  }, [mapping]);

  const handleMappingChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const id = e.target.name;
      const v = e.target.value;
      const fieldById: Record<string, keyof BulkProductUploadColumnMapping> = {
        [MAPPING_SELECT_IDS.title]: 'title',
        [MAPPING_SELECT_IDS.description]: 'description',
        [MAPPING_SELECT_IDS.sku]: 'sku',
        [MAPPING_SELECT_IDS.parentSku]: 'parentSku',
        [MAPPING_SELECT_IDS.combinationString]: 'combinationString',
        [MAPPING_SELECT_IDS.price]: 'price',
        [MAPPING_SELECT_IDS.costPrice]: 'costPrice',
        [MAPPING_SELECT_IDS.quantity]: 'quantity',
        [MAPPING_SELECT_IDS.imageUrl]: 'imageUrl',
        [MAPPING_SELECT_IDS.avatarUrl]: 'avatarUrl',
        [MAPPING_SELECT_IDS.currency]: 'currency',
        [MAPPING_SELECT_IDS.published]: 'published',
        [MAPPING_SELECT_IDS.weight]: 'weight',
        [MAPPING_SELECT_IDS.tags]: 'tags',
        [MAPPING_SELECT_IDS.limitedStock]: 'limitedStock',
        [MAPPING_SELECT_IDS.inhouse]: 'inhouse',
        [MAPPING_SELECT_IDS.categoryName]: 'categoryName',
        [MAPPING_SELECT_IDS.catalogIds]: 'catalogIds',
      };
      for (const [
        n,
        nameId,
        optionsId,
      ] of BULK_VARIATION_NAME_OPTIONS_SELECT_IDS) {
        fieldById[nameId] = `variationName${n}`;
        fieldById[optionsId] = `variationOptions${n}`;
      }
      const field = fieldById[id];
      if (field) {
        setMapping((prev) => ({ ...prev, [field]: v }));
      }
      setParseErrors([]);
    },
    []
  );

  const noopBlur = useCallback(() => {}, []);

  const previewHeaders = useMemo(() => {
    if (!rawRows.length) {
      return [];
    }
    return Object.keys(rawRows[0]);
  }, [rawRows]);

  const previewRows = useMemo(
    () => rawRows.slice(0, PREVIEW_ROW_LIMIT),
    [rawRows]
  );

  const processFile = useCallback((file: File) => {
    setLoadingFile(true);
    setParseErrors([]);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      if (!bstr || typeof bstr !== 'string') {
        setLoadingFile(false);
        setParseErrors(['Could not read the file.']);
        return;
      }
      try {
        const wb = read(bstr, { type: 'binary' });
        const wsname =
          wb.SheetNames.find((n) => n.trim().toLowerCase() === 'products') ??
          wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonRows = utils.sheet_to_json<Record<string, unknown>>(ws, {
          defval: '',
          raw: false,
        });
        if (!jsonRows.length) {
          setParseErrors(['The file must contain at least one row of data.']);
          setLoadingFile(false);
          return;
        }
        const headers = Object.keys(jsonRows[0]);
        if (!headers.length) {
          setParseErrors([
            'The first row of the file must contain column names.',
          ]);
          setLoadingFile(false);
          return;
        }
        setRawRows(jsonRows);
        setMapping(suggestBulkUploadColumnMapping(headers));
        setPhase('map');
      } catch {
        setParseErrors([
          'Could not parse this file. Use a valid CSV or Excel file.',
        ]);
      }
      setLoadingFile(false);
    };
    reader.onerror = () => {
      setLoadingFile(false);
      setParseErrors(['Could not read the file.']);
    };
    reader.readAsBinaryString(file);
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        processFile(file);
      }
    },
    [processFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: loadingFile || submittingUpload,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
    },
  });

  const handleUploadProducts = useCallback(async () => {
    setParseErrors([]);
    setSubmittingUpload(true);
    try {
      if (!user?.id) {
        toast('Sign in again to upload products.', 'error');
        return;
      }
      const defaultCountry = parseDefaultCountry(country);
      if (!defaultCountry) {
        toast(
          'Country required. Update your supplier profile country before bulk upload.',
          'error',
        );
        return;
      }
      if (!currencyOpts) {
        toast(
          'Currency unavailable for your country. Check your profile country code.',
          'error',
        );
        return;
      }
      if (bulkRef === null) {
        toast(
          'Category and tag reference data is still loading. Try again in a moment.',
          'default',
        );
        return;
      }

      const result = parseBulkProductUploadWithMapping(rawRows, mapping, {
        categoryNameToId: bulkRef.categoryNameToId,
        ...(bulkRef.tagNameToCanonical && bulkRef.tagNameToCanonical.size > 0
          ? { tagNameToCanonical: bulkRef.tagNameToCanonical }
          : {}),
        defaultCountry,
        defaultCurrency: currencyOpts.defaultCurrency,
        allowedCurrencyCodes: currencyOpts.allowedCurrencyCodes,
      });

      if (!result.ok) {
        setParseErrors(result.errors);
        return;
      }

      const build = bulkRowsToTempCreateProductInputs(
        result.lines,
        bulkRef.categoryIdToName
      );
      if (!build.ok) {
        setParseErrors(build.errors);
        return;
      }

      const products = enrichSupplierBulkProductInputs(
        build.products,
        user.id,
      );

      const rehosted = await rehostBulkProductMediaUrls(products, {
        folder: 'bulk-products',
        getHeaders: () => {
          const auth = currentTokenVar();
          const c = currentCountryVar();
          return {
            ...(auth ? { authorization: `Bearer ${auth}` } : {}),
            ...(c ? { country: c } : {}),
            source: 'supplier-console',
          };
        },
      });
      if (!rehosted.ok) {
        toast(rehosted.message, 'error');
        return;
      }

      const { data, errors: gqlErrors } = await bulkCreateProductsMutation({
        variables: { products: rehosted.products },
      });

      if (gqlErrors && gqlErrors.length > 0) {
        toast(
          `Bulk create failed: ${gqlErrors.map((e) => e.message).join('; ')}`,
          'error',
        );
        return;
      }

      if (!data?.bulkCreateProducts) {
        toast(
          'Bulk create failed: the server did not confirm product creation.',
          'error',
        );
        return;
      }

      await refetchProducts();
      toast(
        `${build.products.length} product(s) were submitted successfully.`,
        'success',
      );
      setOpen(false);
    } catch (e) {
      toast(
        e instanceof Error ? e.message : 'Bulk upload failed. Try again.',
        'error',
      );
    } finally {
      setSubmittingUpload(false);
    }
  }, [
    bulkCreateProductsMutation,
    bulkRef,
    country,
    currencyOpts,
    mapping,
    rawRows,
    refetchProducts,
    setOpen,
    user?.id,
  ]);

  const handleChooseDifferentFile = useCallback(() => {
    setPhase('pick');
    setRawRows([]);
    setParseErrors([]);
    setMapping({
      title: '',
      description: '',
      sku: '',
      parentSku: '',
      combinationString: '',
      ...emptyBulkProductVariationColumnMapping(),
      price: '',
      costPrice: '',
      quantity: '',
      imageUrl: '',
      avatarUrl: '',
      currency: '',
      published: '',
      weight: '',
      tags: '',
      limitedStock: '',
      inhouse: '',
      categoryName: '',
      catalogIds: '',
    });
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    setTemplateLoading(true);
    try {
      if (!user?.id) {
        toast('Sign in again to download the template.', 'error');
        return;
      }
      const [categories, tagNames] = await Promise.all([
        fetchAllProductCategories(client, { country, supplierId: user.id }),
        fetchAllProductTags(client, { country, supplierId: user.id }),
      ]);
      const currencyCodesList = currencyOpts
        ? Array.from(currencyOpts.allowedCurrencyCodes ?? []).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
          )
        : [];
      if (currencyOpts && currencyCodesList.length === 0) {
        currencyCodesList.push(currencyOpts.defaultCurrency);
      }
      const blob = await buildBulkProductUploadTemplateXlsx(
        categories,
        tagNames,
        currencyCodesList
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk-products-template-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast(
        [
          'Template downloaded.',
          categories.length > 0
            ? `${categories.length} categor${categories.length === 1 ? 'y' : 'ies'} on the Categories sheet (CATEGORY column).`
            : 'No categories yet - add categories and download again for a populated CATEGORY list.',
          tagNames.length > 0
            ? `${tagNames.length} distinct tag${tagNames.length === 1 ? '' : 's'} on the Tags sheet (TAGS column).`
            : 'No tags on products yet - TAGS sheet is omitted until products have tags.',
          currencyCodesList.length > 0
            ? `Currencies sheet: ${currencyCodesList.length} code(s) from selected country context.`
            : 'No currency context available - set CURRENCY manually when needed.',
          'Boolean columns use a TRUE / FALSE dropdown.',
        ].join(' '),
        'success',
      );
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : 'Could not build template. Try again.',
        'error',
      );
    } finally {
      setTemplateLoading(false);
    }
  }, [client, country, currencyOpts, user?.id]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Upload bulk products"
      hideActions
      size="6xl"
    >
      <div className="sm:p-6 space-y-4">
        <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 p-4 border border-amber-200 dark:border-amber-800">
          <div className="flex gap-3">
            <InformationCircleIcon
              className="h-10 w-10 shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden
            />
            <div>
              <h3 className="text-sm font-medium text-amber-900 dark:text-amber-100">
                File requirements
              </h3>
              <ul className="mt-2 text-sm text-amber-800 dark:text-amber-200 list-disc pl-5 space-y-1">
                <li>The file uploaded must be in CSV or Excel format.</li>
                <li>
                  <span className="font-semibold">Variable products</span>: a
                  parent row has a <span className="font-semibold">SKU</span>{' '}
                  and optional{' '}
                  <span className="font-semibold">VARIATION_NAME_</span>
                  <i>n</i> /{' '}
                  <span className="font-semibold">VARIATION_OPTIONS_</span>
                  <i>n</i> pairs. For each sellable combination, add a row with
                  a blank <span className="font-semibold">SKU</span>, fill{' '}
                  <span className="font-semibold">PARENT_SKU</span> and{' '}
                  <span className="font-semibold">COMBINATION_STRING</span>{' '}
                  (e.g. Red&Small) with price, stock, cost, and image. Variant
                  SKUs are generated from the parent SKU.
                </li>
                <li>
                  Download the{' '}
                  <span className="font-semibold">Excel template</span> for a
                  ready-made sheet (required fields plus optional columns). The{' '}
                  <span className="font-semibold">CATEGORY</span> column uses a
                  dropdown of <span className="font-semibold">names</span> from
                  your catalog; the app matches names to ids when you upload.
                </li>
                <li>
                  Required columns to map:{' '}
                  <span className="font-semibold">
                    TITLE, DESCRIPTION, SKU, PRICE, COST PRICE, QUANTITY
                  </span>
                  . Each row also needs at least one valid{' '}
                  <span className="font-semibold">IMAGE URL</span> or an{' '}
                  <span className="font-semibold">AVATAR URL</span> (primary
                  image).
                </li>
                <li>
                  Optional: <span className="font-semibold">CURRENCY</span>,{' '}
                  <span className="font-semibold">PUBLISHED</span>,{' '}
                  <span className="font-semibold">WEIGHT</span>,{' '}
                  <span className="font-semibold">TAGS</span>,{' '}
                  <span className="font-semibold">LIMITED STOCK</span>,{' '}
                  <span className="font-semibold">INHOUSE</span>,{' '}
                  <span className="font-semibold">CATEGORY</span>,{' '}
                  <span className="font-semibold">CATALOG IDS</span>,{' '}
                  <span className="font-semibold">PARENT_SKU</span>,{' '}
                  <span className="font-semibold">COMBINATION_STRING</span>, and
                  parent-only{' '}
                  <span className="font-semibold">VARIATION_NAME_</span>
                  <i>n</i> /{' '}
                  <span className="font-semibold">VARIATION_OPTIONS_</span>
                  <i>n</i>.
                </li>
                <li>
                  The first row of the file must contain the column names.
                </li>
                <li>The file must contain at least one row of data.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <OfficeButton
            variant="tertiary"
            type="button"
            disabled={templateLoading}
            onClick={() => void handleDownloadTemplate()}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2 inline" aria-hidden />
            {templateLoading ? 'Building template…' : 'Download Excel template'}
          </OfficeButton>
        </div>

        {phase === 'pick' ? (
          <>
            <div>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Expected fields (map after upload)
              </span>
              <div className="mt-1 w-full min-h-10 border border-gray-200 dark:border-gray-600 flex flex-wrap gap-x-3 gap-y-1 items-center px-4 py-2 text-xs sm:text-sm bg-slate-50 dark:bg-gray-800/50 rounded-md">
                <span className="font-medium">TITLE</span>
                <span className="font-medium">DESCRIPTION</span>
                <span className="font-medium">SKU</span>
                <span className="text-gray-500 dark:text-gray-400">
                  PARENT_SKU
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  COMBINATION_STRING
                </span>
                <span className="font-medium">PRICE</span>
                <span className="font-medium">COST PRICE</span>
                <span className="font-medium">QUANTITY</span>
                <span className="text-gray-500 dark:text-gray-400">
                  IMAGE URL
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  AVATAR URL
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  CURRENCY
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  PUBLISHED
                </span>
                <span className="text-gray-500 dark:text-gray-400">WEIGHT</span>
                <span className="text-gray-500 dark:text-gray-400">TAGS</span>
                <span className="text-gray-500 dark:text-gray-400">
                  LIMITED STOCK
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  INHOUSE
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  CATEGORY
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  CATALOG IDS
                </span>
              </div>
            </div>

            {loadingFile ? (
              <div className="flex flex-col items-center justify-center py-10">
                <svg
                  className="animate-spin h-8 w-8 text-primary-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A8.003 8.003 0 0112 4.472v3.09a4.001 4.001 0 00-4 3.999H6z"
                  />
                </svg>
                <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  Reading file...
                </p>
              </div>
            ) : (
              <button
                type="button"
                {...getRootProps()}
                className={clsx(
                  'relative block w-full rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-10 text-center hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors',
                  isDragActive &&
                    'border-primary-500 bg-primary-50/50 dark:bg-primary-900/10'
                )}
              >
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m0-4c0 4.418-7.163 8-16 8S8 28.418 8 24m32 10v6m0 0v6m0-6h6m-6 0h-6"
                  />
                </svg>
                <p className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  {isDragActive
                    ? 'Drop the file here'
                    : 'Drag and drop a CSV or Excel file, or click to browse'}
                </p>
                {fileName ? (
                  <p className="mt-1 text-xs text-gray-500">
                    Last file: {fileName}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    .csv, .xls, .xlsx
                  </p>
                )}
                <input {...getInputProps()} />
              </button>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {fileName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {rawRows.length} row(s) loaded - map columns, review the
                  preview, then upload.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  {...getRootProps()}
                  className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Choose different file
                  <input {...getInputProps()} />
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/40 dark:bg-primary-900/15 p-4">
              <p className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-3">
                Map file columns to product fields
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.title}
                    label="Title"
                    required
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsRequired}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.description}
                    label="Description"
                    required
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsRequired}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.sku}
                    label="SKU"
                    required
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsRequired}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.parentSku}
                    label="PARENT_SKU (combination lines)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.combinationString}
                    label="COMBINATION_STRING (combination lines)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <p className="text-xs text-gray-500 col-span-full -mb-1">
                  Parent rows: set <span className="font-medium">SKU</span> and
                  variation axes. For each variant, add a row with only{' '}
                  <span className="font-medium">PARENT_SKU</span> and{' '}
                  <span className="font-medium">COMBINATION_STRING</span> plus
                  price, stock, and image.
                </p>
                <p className="text-xs text-gray-500 col-span-full -mb-1">
                  Variation axes (optional on parent only): map VARIATION_NAME_1
                  / VARIATION_OPTIONS_1 up to 7, or leave unmapped. Options are
                  comma-separated.
                </p>
                {BULK_VARIATION_NAME_OPTIONS_SELECT_IDS.map(
                  ([n, nameId, optionsId]) => (
                    <Fragment key={n}>
                      <div>
                        <SelectInput
                          id={nameId}
                          label={`VARIATION_NAME_${n} (optional)`}
                          values={selectValues}
                          handleChange={handleMappingChange}
                          handleBlur={noopBlur}
                          options={selectOptionsImage}
                        />
                      </div>
                      <div>
                        <SelectInput
                          id={optionsId}
                          label={`VARIATION_OPTIONS_${n} (optional)`}
                          values={selectValues}
                          handleChange={handleMappingChange}
                          handleBlur={noopBlur}
                          options={selectOptionsImage}
                        />
                      </div>
                    </Fragment>
                  )
                )}
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.price}
                    label="Price"
                    required
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsRequired}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.costPrice}
                    label="Cost price"
                    required
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsRequired}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.quantity}
                    label="Quantity"
                    required
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsRequired}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.imageUrl}
                    label="Image URL (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.avatarUrl}
                    label="Avatar URL (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.currency}
                    label="Currency (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.published}
                    label="Published (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.weight}
                    label="Weight (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.tags}
                    label="Tags (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.limitedStock}
                    label="Limited stock (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.inhouse}
                    label="In-house (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.categoryName}
                    label="Category name (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
                <div>
                  <SelectInput
                    id={MAPPING_SELECT_IDS.catalogIds}
                    label="Catalog IDs (optional)"
                    values={selectValues}
                    handleChange={handleMappingChange}
                    handleBlur={noopBlur}
                    options={selectOptionsImage}
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Preview (first {PREVIEW_ROW_LIMIT} rows)
                </span>
                {rawRows.length > PREVIEW_ROW_LIMIT ? (
                  <span className="text-xs text-gray-500">
                    +{rawRows.length - PREVIEW_ROW_LIMIT} more in file
                  </span>
                ) : null}
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-80 overflow-y-auto overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                    <tr>
                      {previewHeaders.map((h, idx) => (
                        <th
                          key={h}
                          scope="col"
                          className={clsx(
                            'px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap',
                            idx === 0 &&
                              'sticky left-0 z-20 bg-gray-50 dark:bg-gray-800'
                          )}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {previewRows.map((row, ri) => (
                      <tr key={ri}>
                        {previewHeaders.map((h, ci) => (
                          <td
                            key={`${ri}-${h}`}
                            className={clsx(
                              'px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300 max-w-[200px] truncate',
                              ci === 0 &&
                                'sticky left-0 z-10 bg-white dark:bg-gray-900'
                            )}
                            title={String(row[h] ?? '')}
                          >
                            {row[h] === '' || row[h] == null
                              ? '—'
                              : String(row[h])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <OfficeButton
                variant="secondary"
                type="button"
                disabled={submittingUpload}
                onClick={handleChooseDifferentFile}
              >
                Start over
              </OfficeButton>
              <OfficeButton
                variant="primary"
                type="button"
                disabled={bulkRef === null || submittingUpload}
                onClick={() => void handleUploadProducts()}
              >
                {submittingUpload ? 'Uploading…' : 'Upload products'}
              </OfficeButton>
            </div>
          </div>
        )}

        {parseErrors.length > 0 ? (
          <div
            className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800 max-h-48 overflow-y-auto"
            role="alert"
          >
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              Fix the following and try again:
            </p>
            <ul className="mt-2 text-sm text-red-700 dark:text-red-300 list-disc pl-5 space-y-1">
              {parseErrors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default BulkUploadProductsModal;
